// Part B (2026-09-25) — Inbox, Pipeline, Live, the tab bar and the agent pill.
// French string → English. The stage, reason and chip labels come from the
// worker (lib/clientStage.ts, lib/inbox.ts) in French and are translated here.
const m: Record<string, string> = {
  // ── Page, tabs, pill ────────────────────────────────────────────────────
  'Ce qui attend une personne, où en est chaque client, et l’agent au travail.':
    'What needs a person, where every client stands, and the agent at work.',
  'Sections de l’assistant': 'Assistant sections',
  'Inbox': 'Inbox',
  'En direct': 'Live',
  'Automatisations': 'Automations',
  'Réglages': 'Settings',
  'Règles et réponses': 'Rules & replies',
  '{n} à traiter maintenant': '{n} need you now',
  'Agent': 'Agent',
  'Voir l’agent en direct': 'See the agent live',
  '{n} conversation(s)': '{n} chat(s)',
  'dernière action il y a {w}': 'last action {w} ago',
  'Agent bloqué — un client attend depuis {w}': 'Agent stuck — a client has waited {w}',
  'Réservé aux réglages': 'Settings permission needed',
  'Personne ne reçoit de message': 'Nobody receives messages',
  'L’envoi est activé mais la liste de test est vide : chaque réponse attend un humain.':
    'Sending is on but the test list is empty: every reply waits for a person.',
  'Tout le monde reçoit une réponse automatique, sans relecture.': 'Everyone gets an automatic reply, unreviewed.',

  // ── Inbox ───────────────────────────────────────────────────────────────
  'À traiter': 'To handle',
  'À traiter maintenant': 'Needs you now',
  'Aujourd’hui': 'Today',
  'Plus anciens que 7 jours': 'Older than 7 days',
  'Plus ancien que 7 jours': 'Older than 7 days',
  'Rien n’attend une personne.': 'Nothing is waiting for a person.',
  '{a} client(s) maintenant · {b} aujourd’hui · {c} plus anciens': '{a} client(s) now · {b} today · {c} older',
  '{h} traités · {i} ignorés · {s} en rappel': '{h} handled · {i} ignored · {s} snoozed',
  'Journal': 'Log',
  'Renvoyer la réponse': 'Send the reply again',
  'Réponse remise dans la file — elle part au prochain passage.': 'Reply queued again — it goes out on the next pass.',
  'Raccourcis (?)': 'Shortcuts (?)',
  'Clients à traiter': 'Clients to handle',
  'Tout est à jour.': 'All caught up.',
  'Un client sans réponse depuis 10 minutes apparaîtra ici tout seul.':
    'A client left unanswered for 10 minutes will show up here on their own.',
  '+ {n} autre(s)': '+ {n} more',
  'souvent d’avant que l’agent réponde à tout le monde.': 'mostly from before the agent answered everyone.',
  'Tout ignorer…': 'Ignore all…',
  'Raison (ex. avant l’ouverture de l’agent à tous)': 'Reason (e.g. before the agent answered everyone)',
  'Ignorer les {n}': 'Ignore the {n}',
  'Chacun revient si le client écrit à nouveau. Tout est dans le journal, annulable.':
    'Each comes back if the client writes again. Everything is in the log, undoable.',
  'en attente depuis {w}': 'waiting {w}',
  'Pourquoi c’est ici': 'Why it is here',
  'Ouvrir la conversation': 'Open the chat',
  'Tout traité': 'All handled',
  'Rappeler': 'Snooze',
  'Pourquoi ? (facultatif — sert à créer des règles)': 'Why? (optional — used to make rules)',
  'Ex. pas un client, message automatique…': 'E.g. not a client, an automatic message…',
  'Il revient si le client écrit à nouveau. Pour ne plus relancer ce client du tout, utilisez « Ignorer le client » sur sa commande.':
    'It comes back if the client writes again. To stop chasing this client altogether, use “Ignore client” on the order.',
  'Ignorer cet élément': 'Ignore this item',
  'Revient à l’heure choisie — ou dès que le client écrit.': 'Comes back at the time you pick — or as soon as the client writes.',
  'Dans 1 h': 'In 1 h',
  'Dans 3 h': 'In 3 h',
  'Demain 9 h': 'Tomorrow 9:00',
  'Autres suivis': 'Other follow-ups',
  'Traité': 'Handled',
  'Ignoré': 'Ignored',
  'Rappel programmé': 'Snoozed',
  '{n} éléments': '{n} items',
  'Raccourcis clavier': 'Keyboard shortcuts',
  'Suivant / précédent': 'Next / previous',
  'Rappeler plus tard': 'Snooze',
  'Annuler la dernière action': 'Undo the last action',
  'Cette aide': 'This help',
  'a traité': 'handled',
  'a ignoré': 'ignored',
  'a mis en rappel': 'snoozed',
  'a annulé': 'undid',
  'quelqu’un': 'someone',
  'Rien pour le moment.': 'Nothing yet.',
  'Impossible de charger l’Inbox.': 'Could not load the Inbox.',
  'L’Inbox arrive avec la nouvelle version du serveur.': 'The Inbox arrives with the new server version.',
  'Faits': 'Facts',
  'Sans nom': 'No name',

  // Reasons (lib/inbox.ts) and their titles
  'Client mécontent': 'Unhappy client',
  'L’IA demande un humain': 'The AI asks for a person',
  'Réponse bloquée': 'Reply held',
  'Sans réponse': 'No reply',
  'En pause — le client attend': 'Paused — the client is waiting',
  'Photos à classer': 'Photos to file',
  'Bloqué chez nous': 'Stuck on our side',
  'Alerte': 'Alert',
  'Personne n’a répondu': 'Nobody replied',
  // Owner, 2026-10-03: one title for every client still waiting, whatever the reason.
  'Client en attente — sans réponse': 'Client waiting — no reply',
  'Traité — l’IA reprend': 'Handled — the AI is back on',
  'L’IA a passé la main — personne n’a répondu': 'The AI handed over — nobody replied',
  'Un humain a la main — le client attend sa réponse': 'A person has the chat — the client is waiting for them',
  'Le client demande à annuler': 'The client asks to cancel',
  'Relances sans réponse — le client ne répond plus': 'Follow-ups unanswered — the client went quiet',
  'L’IA allait se répéter — bloquée deux fois': 'The AI was about to repeat itself — blocked twice',
  'Un message n’est pas parti': 'A message did not go out',
  'Une photo du client est perdue': 'A client photo is lost',
  'Fichier reçu illisible': 'Unreadable file received',
  'Album retouché à la main — l’agent ne modifie plus l’album (Off) tant que tu ne le rallumes pas': 'Album edited by hand — agent album edits are Off until you switch them back on',
  'Remarque sur l’aperçu à traiter à la main': 'Preview note to handle by hand',
  'Paiement à valider': 'Payment to approve',
  'Demande d’annulation': 'Cancellation request',
  'Photos retenues': 'Photos held',
  'Demande d’album en plus': 'Extra album requested',
  'Ajout d’album à vérifier': 'Added album to check',
  'Albums nommés dans le chat — à vérifier': 'Albums named in the chat — to check',
  'Le rendu de l’album a abandonné': 'The album render gave up',
  'Capture d’écran à vérifier': 'Screenshot to check',
  'Remarque en capture d’écran': 'Note in a screenshot',
  'Retouche d’album à valider': 'Album edit to review',
  'Lien annoncé mais absent': 'Link announced but missing',
  'Cliente contente — lien d’avis Google non configuré': 'Happy client — no Google review link set',
  'Question sur la production': 'Production question',

  // ── Pipeline ────────────────────────────────────────────────────────────
  'Pipeline': 'Pipeline',
  '{n} commandes en cours · {m} MAD · {s} bloquées chez nous · {l} contacts':
    '{n} orders in progress · {m} MAD · {s} stuck on our side · {l} contacts',
  'Chercher un client': 'Find a client',
  'Nom, commande ou téléphone': 'Name, order or phone',
  'Annulés et ignorés': 'Cancelled & ignored',
  '+ {n} plus anciens': '+ {n} older',
  'Personne': 'Nobody',
  'Hors du tableau — commandes annulées': 'Off the board — cancelled orders',
  'depuis {w}': 'for {w}',
  'Voir la commande': 'Open the order',
  'Ses autres commandes': 'Their other orders',
  'Annulées : {list}': 'Cancelled: {list}',
  'Tout ce qui est ici vient de la commande, des albums, de l’argent enregistré et des messages que WhatsApp a acceptés. Ce que l’IA a dit ou deviné n’avance jamais un client.':
    'Everything here comes from the order, the albums, the money recorded and the messages WhatsApp accepted. Nothing the AI said or guessed ever moves a client.',
  'Impossible de charger le pipeline.': 'Could not load the pipeline.',
  'Le pipeline arrive avec la nouvelle version du serveur.': 'The pipeline arrives with the new server version.',
  '{n} fait(s)': '{n} done',
  'à nous': 'ours',
  'au client': 'client’s',
  'au livreur': 'courier’s',
  'bloqué': 'stuck',
  'Revient': 'Returning',
  'Réclamation': 'Complaint',
  'Un humain a la main': 'A person has it',
  'Annulé': 'Cancelled',

  // Stages and phases (lib/clientStage.ts)
  'Avant la commande': 'Before the order',
  'Commande & photos': 'Order & photos',
  'Album & validation': 'Album & approval',
  'Paiement & livraison': 'Payment & delivery',
  'Nouveau contact': 'New lead',
  'Discussion': 'Talking',
  'Commande créée': 'Order created',
  'Couverture choisie': 'Cover chosen',
  'Photos reçues': 'Photos stored',
  'Album monté': 'Album built',
  'Aperçu envoyé': 'Preview sent',
  'Validé': 'Approved',
  'Paiement reçu': 'Paid',
  'Impression': 'Printing',
  'En route': 'Shipped',
  'Livré': 'Delivered',

  // Fact chips
  'Couverture': 'Cover',
  'Photos': 'Photos',
  'Album': 'Album',
  'Aperçu': 'Preview',
  'Validation': 'Approval',
  'Paiement à la livraison': 'Pays on delivery',
  'Payé à la livraison': 'Paid on delivery',
  'Payé par virement': 'Paid by transfer',
  'Virement attendu': 'Transfer awaited',
  'Payé': 'Paid',
  'Mode de paiement non renseigné': 'Payment method not set',
  'Validé sur l’aperçu': 'Approved on the preview',
  'Validé dans le chat': 'Approved in the chat',
  'Validé par l’équipe': 'Approved by the team',
  'Modifications demandées': 'Changes requested',
  'Aperçu marqué envoyé — aucun message confirmé': 'Preview marked sent — no confirmed message',
  'D’autres photos annoncées (lu par l’IA)': 'More photos announced (the AI’s reading)',

  // ── Live ────────────────────────────────────────────────────────────────
  'L’agent ne bouge plus.': 'The agent has stopped.',
  'Un client attend depuis {w} et l’agent n’a rien fait depuis. Vérifiez la ligne WhatsApp et les crédits IA.':
    'A client has waited {w} and the agent has done nothing since. Check the WhatsApp line and the AI credits.',
  'conversations que l’agent suit en ce moment': 'chats the agent is handling right now',
  'Réponses aujourd’hui': 'Replies today',
  'Retenues pour vous': 'Held for you',
  'Commandes créées': 'Orders created',
  'Coût IA aujourd’hui': 'AI cost today',
  '« Retenues pour vous » : l’agent s’est arrêté de lui-même (lien manquant, prix incertain, demande d’un humain). Chacune est déjà dans l’Inbox.':
    '“Held for you”: the agent stopped itself (missing link, unsure price, asked for a person). Each one is already in the Inbox.',
  'Ce que l’agent vient de faire': 'What the agent just did',
  'Rien dans les dernières 24 heures.': 'Nothing in the last 24 hours.',
  'Pourquoi l’agent passe la main': 'Why the agent hands over',
  'Ce classement est la liste de ce qu’il faut automatiser ensuite.': 'This ranking is the list of what to automate next.',
  'Aucun passage de main enregistré.': 'No handover recorded.',
  '{m} messages aujourd’hui · {p} conversation(s) tenue(s) par un humain': '{m} messages today · {p} chat(s) held by a person',
  'Réponse': 'Reply',
  'Passe la main': 'Hands over',
  'Retenue': 'Held',
  'Commande': 'Order',
  'A passé la main à un humain': 'Handed over to a person',
  'Réponse envoyée': 'Reply sent',
  'Impossible de charger le direct.': 'Could not load Live.',
  'Le direct arrive avec la nouvelle version du serveur.': 'Live arrives with the new server version.',

  // ── Timeline (2026-09-26: 7 days by default) ────────────────────────────
  '{n} client(s) hors de cette période': '{n} client(s) outside this period',
  '{n} conversation(s) hors de cette période': '{n} conversation(s) outside this period',
  'Tout afficher': 'Show all',
  'Rien dans cette période.': 'Nothing in this period.',
  '{n} client(s) plus anciens attendent encore.': '{n} older client(s) still waiting.',
  // ── Conversation header buttons (2026-10-07) ────────────────────────────
  'Choisir l’heure': 'Pick a time',
  'Ce soir 20 h': 'Tonight 8 pm',
  '{n} à traiter': '{n} to handle',
  'Rien à traiter pour cette conversation.': 'Nothing to handle in this conversation.',
  'la conversation suivante s’ouvre…': 'the next conversation opens…',
  'Détails : {code}': 'Details: {code}',
  'Elle veut': 'She wants',
  'Agrandir': 'Expand',
  'Réduire': 'Shrink',
  'Agrandir la conversation': 'Show the chat full width',
  'Réafficher la liste et la commande': 'Show the list and the order again',
  'Cliente pas contente ou qui hésite': 'Client unhappy or hesitating',
  'Elle n’est pas contente, ou hésite à continuer': 'She is not happy, or not sure she will go on',
  'Lui répondre vous-même : comprendre ce qui ne lui plaît pas, et proposer une solution avant qu’elle annule.': 'Answer her yourself: find out what she dislikes, and offer a fix before she cancels.',
  'Décider : une dernière relance à la main, ou laisser tomber.': 'Decide: one last reminder by hand, or let it go.',
  'Lire la conversation et lui répondre vous-même.': 'Read the conversation and answer her yourself.',
  'Lui renvoyer le message qui n’est pas parti.': 'Send her again the message that did not go out.',
  'Lui demander de renvoyer ses photos en Document (📎 → Document).': 'Ask her to send her photos again as a Document (📎 → Document).',
  'Lui demander de renvoyer le fichier en photo ou en PDF.': 'Ask her to send the file again as a photo or a PDF.',
  'Finir vos retouches, puis rallumer « L’agent modifie l’album ».': 'Finish your edits, then turn « Agent edits the album » back on.',
  'Faire la retouche demandée dans l’éditeur.': 'Make the requested edit in the editor.',
  'Vérifier le paiement sur le compte, puis valider ou refuser.': 'Check the payment on the account, then approve or refuse.',
  'Confirmer ou refuser l’annulation avec elle.': 'Confirm or refuse the cancellation with her.',
  'Ranger ses photos dans le bon album.': 'Put her photos in the right album.',
  'Placer les nouvelles photos dans l’album.': 'Place the new photos in the album.',
  'Ajouter l’album demandé à sa commande.': 'Add the requested album to her order.',
  'Vérifier les albums de la commande.': 'Check the order’s albums.',
  'Vérifier les albums nommés et les ajouter.': 'Check the albums she named and add them.',
  'Relancer le rendu de l’album.': 'Start the album render again.',
  'Regarder la capture et lui répondre.': 'Look at the screenshot and answer her.',
  'Lire la remarque en capture et faire la retouche.': 'Read the note in the screenshot and make the edit.',
  'Lire la retouche prévue et dire oui ou non.': 'Read the planned edit and say yes or no.',
  'Lui envoyer le lien annoncé.': 'Send her the link we announced.',
  'Mettre le lien d’avis Google dans les réglages.': 'Put the Google review link in Settings.',
  'S’excuser, lui demander ce qui ne va pas, puis décider de la suite (un geste, c’est vous qui décidez).': 'Apologise, ask her what is wrong, then decide what next (any gesture is your call).',
  'Lui répondre vous-même.': 'Answer her yourself.',
  'Lire la réponse retenue de l’IA, puis l’envoyer ou la corriger.': 'Read the AI’s held reply, then send it or fix it.',
  'Lui répondre — personne ne l’a fait.': 'Answer her — nobody has.',
  'Vous avez la main : lui répondre, ou rendre la conversation à l’IA (✓).': 'You have the chat: answer her, or hand it back to the AI (✓).',
  'Ranger ses photos dans un album.': 'Put her photos in an album.',
  'Faire avancer la commande à l’étape suivante.': 'Move the order to its next step.',
  'Lire l’alerte et agir.': 'Read the alert and act.',
  'Ouvrir dans WhatsApp': 'Open in WhatsApp',
  'Glissez à gauche ou à droite pour la phase suivante.': 'Swipe left or right for the next phase.',
  'Revient dans l’Inbox à l’heure choisie — ou dès que le client écrit.': 'Comes back to the Inbox at the chosen time — or as soon as the client writes.',
};
export default m;
